/**
 * DeliveryDateCard — when this order actually arrives, as one compact line.
 *
 * Shaped like the "Secured by Razorpay · UPI, cards & netbanking" strip below
 * it: icon, the fact in bold, then a muted qualifier after a middot. It is a
 * single sentence of information, so it gets a single line — the two blocks
 * that need real attention (delivery details, bill) are the tinted cards.
 *
 * WHAT IT REPLACED
 * ----------------
 * Checkout used to show a fixed line: "Order now — arrives Today, 7:00 PM".
 * That was hardcoded, and false for any order placed after the college's
 * cutoff — those go out with the NEXT evening's batch, not tonight's. A
 * student ordering at 6 PM was promised a delivery that could not happen.
 *
 * Now the day is derived from the same batch maths the admin Excel export
 * groups by (`describeDelivery` in @rocket07/shared), so the promise made at
 * checkout and the batch the order lands in can never disagree.
 *
 * WHY IT RECOMPUTES ON A TIMER
 * ----------------------------
 * The answer flips the moment the cutoff passes. Someone who opens checkout at
 * 3:58 PM and pays at 4:01 PM must not still be reading "Today" — the server
 * will have put them in tomorrow's batch. Re-deriving every 30 seconds keeps
 * the card honest for as long as the page stays open.
 *
 * Times come from serverNow(), not the device clock, so a phone with the wrong
 * time still sees the right day.
 */

import { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { describeDelivery, formatDuration } from '@rocket07/shared';
import { serverNow } from '@/lib/serverClock';

/** Matches the College model's default (960 = 4:00 PM IST). */
const DEFAULT_CUTOFF_MINUTES = 960;

/** Below this, the cutoff is close enough that a countdown is useful. */
const COUNTDOWN_THRESHOLD_MINUTES = 360; // 6 hours

export function DeliveryDateCard({ cutoffMinutes = DEFAULT_CUTOFF_MINUTES }) {
  const [delivery, setDelivery] = useState(() => describeDelivery(cutoffMinutes, serverNow()));

  useEffect(() => {
    // Re-derive immediately too: `cutoffMinutes` arrives a beat after the first
    // render, once the college list has loaded.
    setDelivery(describeDelivery(cutoffMinutes, serverNow()));

    const id = setInterval(() => {
      setDelivery(describeDelivery(cutoffMinutes, serverNow()));
    }, 30_000);
    return () => clearInterval(id);
  }, [cutoffMinutes]);

  const { dayLabel, dateLabel, cutoffLabel, isToday, minutesToCutoff } = delivery;

  /**
   * Kept short on purpose. This has to survive on one line on a 360px phone
   * next to the date, so it says the one thing that changes the student's
   * decision — how long they have — and drops the rest.
   */
  const cutoffNote = !isToday
    ? // Not "today's 4:00 PM cutoff has passed": with the longest date this
      // can produce ("Tomorrow, 30 Sept") that wraps to a second line on a
      // 360px phone, and the exact time matters least once it has gone.
      'cutoff passed'
    : minutesToCutoff <= COUNTDOWN_THRESHOLD_MINUTES
      ? `order within ${formatDuration(minutesToCutoff)}`
      : // "order by", not "order before": with the longest date ("Today,
        // 30 Sept") the extra four characters are exactly what tips this onto
        // a second line at 360px.
        `order by ${cutoffLabel}`;

  return (
    <div
      role="status"
      className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-xl border border-line bg-surface px-3.5 py-2.5 shadow-card"
    >
      <CalendarClock size={16} className="shrink-0 text-primary-600" />
      <span className="text-xs font-semibold text-ink">
        Arriving {dayLabel}, {dateLabel}
      </span>
      <span className="text-xs text-muted">· {cutoffNote}</span>
    </div>
  );
}

export default DeliveryDateCard;
